// ---------------------------------------------------------------------------
// Digital Notice Board — PropertiesPanel (right inspector)
// ---------------------------------------------------------------------------
// Context-sensitive editor for whatever is selected. With no selection it edits
// the slide itself (name / background / on-screen duration). Every control calls
// onChange with a shallow patch; the page merges it into the element/slide. Kept
// deliberately verbose-but-flat so a non-developer can find each control.
// ---------------------------------------------------------------------------
import React from "react";
import {
    Box,
    Button,
    Divider,
    Flex,
    HStack,
    IconButton,
    Input,
    Select,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Switch,
    Text,
    Textarea,
    useColorModeValue,
    VStack,
} from "@chakra-ui/react";
import {
    FaAlignCenter,
    FaAlignLeft,
    FaAlignRight,
    FaArrowDown,
    FaArrowUp,
    FaClone,
    FaTrash,
} from "react-icons/fa";
import {
    COLOR_SWATCHES,
    ELEMENT_TYPES,
    FONT_FAMILIES,
} from "../constants/NoticeBoardConstants";
import {ICON_CATALOG} from "../constants/iconCatalog";

const Label = ({children}) => (
    <Text fontSize="11px" fontWeight="bold" color="gray.400" mb="4px" letterSpacing="0.4px">
        {children}
    </Text>
);

function ColorField({label, value, onChange}) {
    return (
        <Box>
            <Label>{label}</Label>
            <Flex wrap="wrap" gap="6px" align="center">
                {COLOR_SWATCHES.map((c) => (
                    <Box
                        key={c}
                        w="20px"
                        h="20px"
                        bg={c}
                        borderRadius="4px"
                        cursor="pointer"
                        border={value === c ? "2px solid #3182CE" : "1px solid #CBD5E0"}
                        onClick={() => onChange(c)}
                    />
                ))}
                <Input
                    type="color"
                    value={/^#/.test(value || "") ? value : "#000000"}
                    onChange={(e) => onChange(e.target.value)}
                    w="28px"
                    h="24px"
                    p="0"
                    border="none"
                    cursor="pointer"
                />
            </Flex>
        </Box>
    );
}

function NumberField({label, value, min, max, step, onChange}) {
    return (
        <Box>
            <Label>{label}</Label>
            <Input
                size="sm"
                type="number"
                value={value}
                min={min}
                max={max}
                step={step || 1}
                onChange={(e) => onChange(Number(e.target.value))}
            />
        </Box>
    );
}

function AlignField({value, onChange}) {
    const opts = [
        {v: "left", Icon: FaAlignLeft},
        {v: "center", Icon: FaAlignCenter},
        {v: "right", Icon: FaAlignRight},
    ];
    return (
        <Box>
            <Label>ALIGN</Label>
            <HStack spacing="4px">
                {opts.map(({v, Icon}) => (
                    <IconButton
                        key={v}
                        size="sm"
                        aria-label={v}
                        icon={<Icon />}
                        colorScheme={value === v ? "blue" : "gray"}
                        variant={value === v ? "solid" : "outline"}
                        onClick={() => onChange(v)}
                    />
                ))}
            </HStack>
        </Box>
    );
}

// --- Slide-level editor (shown when nothing is selected) --------------------
function SlideInspector({slide, onSlideChange}) {
    return (
        <VStack align="stretch" spacing="14px">
            <Text fontSize="sm" fontWeight="bold" color="gray.500">
                Slide settings
            </Text>
            <Box>
                <Label>SLIDE NAME</Label>
                <Input
                    size="sm"
                    value={slide.name}
                    onChange={(e) => onSlideChange({name: e.target.value})}
                />
            </Box>
            <ColorField
                label="BACKGROUND"
                value={slide.background}
                onChange={(v) => onSlideChange({background: v})}
            />
            <NumberField
                label="DISPLAY DURATION (SECONDS)"
                value={slide.duration}
                min={2}
                max={120}
                onChange={(v) => onSlideChange({duration: v})}
            />
            <Text fontSize="11px" color="gray.400">
                Select an element on the screen to edit it, or drag a new element from the
                left.
            </Text>
        </VStack>
    );
}

export default function PropertiesPanel({
    element,
    slide,
    onChange,
    onSlideChange,
    onDelete,
    onDuplicate,
    onLayer,
}) {
    const border = useColorModeValue("gray.200", "gray.600");

    if (!element) {
        return <SlideInspector slide={slide} onSlideChange={onSlideChange} />;
    }

    const t = element.type;
    const isText = t === ELEMENT_TYPES.TEXT || t === ELEMENT_TYPES.HEADING;
    const isTimeText = t === ELEMENT_TYPES.CLOCK || t === ELEMENT_TYPES.DATE;

    return (
        <VStack align="stretch" spacing="14px">
            <Flex justify="space-between" align="center">
                <Text fontSize="sm" fontWeight="bold" color="gray.500" textTransform="capitalize">
                    {t} settings
                </Text>
                <HStack spacing="4px">
                    <IconButton size="xs" aria-label="Forward" icon={<FaArrowUp />} onClick={() => onLayer("front")} />
                    <IconButton size="xs" aria-label="Backward" icon={<FaArrowDown />} onClick={() => onLayer("back")} />
                    <IconButton size="xs" aria-label="Duplicate" icon={<FaClone />} onClick={onDuplicate} />
                    <IconButton size="xs" aria-label="Delete" colorScheme="red" icon={<FaTrash />} onClick={onDelete} />
                </HStack>
            </Flex>
            <Divider borderColor={border} />

            {/* ---- Text / Heading ---- */}
            {isText && (
                <>
                    <Box>
                        <Label>TEXT</Label>
                        <Textarea
                            size="sm"
                            rows={3}
                            value={element.text}
                            onChange={(e) => onChange({text: e.target.value})}
                        />
                    </Box>
                    <HStack>
                        <NumberField label="SIZE" value={element.fontSize} min={8} max={400} onChange={(v) => onChange({fontSize: v})} />
                        <Box flex="1">
                            <Label>WEIGHT</Label>
                            <Select size="sm" value={element.fontWeight} onChange={(e) => onChange({fontWeight: Number(e.target.value)})}>
                                <option value={300}>Light</option>
                                <option value={400}>Regular</option>
                                <option value={600}>Semibold</option>
                                <option value={700}>Bold</option>
                                <option value={900}>Black</option>
                            </Select>
                        </Box>
                    </HStack>
                    <Box>
                        <Label>FONT</Label>
                        <Select size="sm" value={element.fontFamily} onChange={(e) => onChange({fontFamily: e.target.value})}>
                            {FONT_FAMILIES.map((f) => (
                                <option key={f} value={f}>{f.split(",")[0].replace(/'/g, "")}</option>
                            ))}
                        </Select>
                    </Box>
                    <AlignField value={element.align} onChange={(v) => onChange({align: v})} />
                    <ColorField label="TEXT COLOR" value={element.color} onChange={(v) => onChange({color: v})} />
                    <ColorField label="BACKGROUND" value={element.bg} onChange={(v) => onChange({bg: v})} />
                </>
            )}

            {/* ---- Marquee ---- */}
            {t === ELEMENT_TYPES.MARQUEE && (
                <>
                    <Box>
                        <Label>SCROLLING TEXT</Label>
                        <Textarea size="sm" rows={2} value={element.text} onChange={(e) => onChange({text: e.target.value})} />
                    </Box>
                    <HStack>
                        <NumberField label="SIZE" value={element.fontSize} min={8} max={200} onChange={(v) => onChange({fontSize: v})} />
                        <NumberField label="SPEED (S)" value={element.speed} min={3} max={60} onChange={(v) => onChange({speed: v})} />
                    </HStack>
                    <ColorField label="TEXT COLOR" value={element.color} onChange={(v) => onChange({color: v})} />
                    <ColorField label="BAR COLOR" value={element.bg} onChange={(v) => onChange({bg: v})} />
                </>
            )}

            {/* ---- Notice card ---- */}
            {t === ELEMENT_TYPES.NOTICE && (
                <>
                    <Box>
                        <Label>TITLE</Label>
                        <Input size="sm" value={element.title} onChange={(e) => onChange({title: e.target.value})} />
                    </Box>
                    <Box>
                        <Label>BODY</Label>
                        <Textarea size="sm" rows={4} value={element.body} onChange={(e) => onChange({body: e.target.value})} />
                    </Box>
                    <HStack>
                        <NumberField label="TITLE SIZE" value={element.titleSize} min={12} max={200} onChange={(v) => onChange({titleSize: v})} />
                        <NumberField label="BODY SIZE" value={element.bodySize} min={10} max={160} onChange={(v) => onChange({bodySize: v})} />
                    </HStack>
                    <ColorField label="ACCENT" value={element.accent} onChange={(v) => onChange({accent: v})} />
                    <ColorField label="CARD COLOR" value={element.bg} onChange={(v) => onChange({bg: v})} />
                    <ColorField label="TEXT COLOR" value={element.color} onChange={(v) => onChange({color: v})} />
                    <NumberField label="CORNER RADIUS" value={element.radius} min={0} max={80} onChange={(v) => onChange({radius: v})} />
                </>
            )}

            {/* ---- Image ---- */}
            {t === ELEMENT_TYPES.IMAGE && (
                <>
                    <Box>
                        <Label>IMAGE URL</Label>
                        <Input size="sm" placeholder="https://…" value={element.src} onChange={(e) => onChange({src: e.target.value})} />
                    </Box>
                    <Box>
                        <Label>OR UPLOAD</Label>
                        <Input
                            size="sm"
                            type="file"
                            accept="image/*"
                            p="4px"
                            onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = () => onChange({src: reader.result});
                                reader.readAsDataURL(file);
                            }}
                        />
                    </Box>
                    <Box>
                        <Label>FIT</Label>
                        <Select size="sm" value={element.fit} onChange={(e) => onChange({fit: e.target.value})}>
                            <option value="cover">Cover</option>
                            <option value="contain">Contain</option>
                            <option value="fill">Stretch</option>
                        </Select>
                    </Box>
                    <NumberField label="CORNER RADIUS" value={element.radius} min={0} max={200} onChange={(v) => onChange({radius: v})} />
                </>
            )}

            {/* ---- Icon ---- */}
            {t === ELEMENT_TYPES.ICON && (
                <>
                    <Label>PICK AN ICON</Label>
                    <Flex wrap="wrap" gap="6px">
                        {Object.entries(ICON_CATALOG).map(([key, {Icon, label}]) => (
                            <Box
                                key={key}
                                title={label}
                                onClick={() => onChange({icon: key})}
                                p="7px"
                                borderRadius="8px"
                                cursor="pointer"
                                color={element.icon === key ? "white" : "gray.500"}
                                bg={element.icon === key ? "blue.400" : "gray.100"}
                            >
                                <Icon size={18} />
                            </Box>
                        ))}
                    </Flex>
                    <ColorField label="COLOR" value={element.color} onChange={(v) => onChange({color: v})} />
                </>
            )}

            {/* ---- Shape ---- */}
            {t === ELEMENT_TYPES.SHAPE && (
                <>
                    <Box>
                        <Label>SHAPE</Label>
                        <Select size="sm" value={element.shape} onChange={(e) => onChange({shape: e.target.value})}>
                            <option value="rect">Rectangle</option>
                            <option value="circle">Circle / Ellipse</option>
                        </Select>
                    </Box>
                    <ColorField label="FILL" value={element.fill} onChange={(v) => onChange({fill: v})} />
                    {element.shape !== "circle" && (
                        <NumberField label="CORNER RADIUS" value={element.radius} min={0} max={300} onChange={(v) => onChange({radius: v})} />
                    )}
                </>
            )}

            {/* ---- Clock / Date ---- */}
            {isTimeText && (
                <>
                    <NumberField label="SIZE" value={element.fontSize} min={12} max={400} onChange={(v) => onChange({fontSize: v})} />
                    <Box>
                        <Label>FONT</Label>
                        <Select size="sm" value={element.fontFamily} onChange={(e) => onChange({fontFamily: e.target.value})}>
                            {FONT_FAMILIES.map((f) => (
                                <option key={f} value={f}>{f.split(",")[0].replace(/'/g, "")}</option>
                            ))}
                        </Select>
                    </Box>
                    <AlignField value={element.align} onChange={(v) => onChange({align: v})} />
                    <ColorField label="COLOR" value={element.color} onChange={(v) => onChange({color: v})} />
                    {t === ELEMENT_TYPES.CLOCK && (
                        <Flex justify="space-between" align="center">
                            <Label>SHOW SECONDS</Label>
                            <Switch
                                isChecked={!!element.seconds}
                                onChange={(e) => onChange({seconds: e.target.checked})}
                            />
                        </Flex>
                    )}
                </>
            )}

            <Divider borderColor={border} />
            <Box>
                <Label>OPACITY</Label>
                <Slider value={(element.opacity != null ? element.opacity : 1) * 100} min={10} max={100} onChange={(v) => onChange({opacity: v / 100})}>
                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                    <SliderThumb />
                </Slider>
            </Box>
            <Box>
                <Label>ROTATION</Label>
                <Slider value={element.rotation || 0} min={-180} max={180} onChange={(v) => onChange({rotation: v})}>
                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                    <SliderThumb />
                </Slider>
            </Box>

            <Button size="sm" leftIcon={<FaTrash />} colorScheme="red" variant="outline" onClick={onDelete}>
                Delete element
            </Button>
        </VStack>
    );
}
